import { useEffect, useRef, ReactNode } from "react";

interface MagnetProps {
  children: ReactNode;
  padding?: number;
  disabled?: boolean;
  magnetStrength?: number;
  activeTransition?: string;
  inactiveTransition?: string;
}

const Magnet: React.FC<MagnetProps> = ({
  children,
  padding = 100,
  disabled = false,
  magnetStrength = 2,
  activeTransition = "transform 120ms cubic-bezier(0.22, 1, 0.36, 1)",
  inactiveTransition = "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
}) => {
  const magnetRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const currentPositionRef = useRef({ x: 0, y: 0 });
  const targetPositionRef = useRef({ x: 0, y: 0 });
  const isActiveRef = useRef(false);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    const applyTransform = () => {
      const { x, y } = currentPositionRef.current;
      inner.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      animationFrameRef.current = null;
    };

    const animateTowardsTarget = () => {
      const current = currentPositionRef.current;
      const target = targetPositionRef.current;
      const nextX = current.x + (target.x - current.x) * 0.18;
      const nextY = current.y + (target.y - current.y) * 0.18;

      currentPositionRef.current = { x: nextX, y: nextY };
      applyTransform();

      if (
        Math.abs(target.x - nextX) > 0.2 ||
        Math.abs(target.y - nextY) > 0.2
      ) {
        animationFrameRef.current = window.requestAnimationFrame(
          animateTowardsTarget
        );
      } else {
        currentPositionRef.current = target;
        applyTransform();
      }
    };

    const scheduleAnimation = () => {
      if (animationFrameRef.current !== null) return;
      animationFrameRef.current = window.requestAnimationFrame(
        animateTowardsTarget
      );
    };

    if (disabled) {
      targetPositionRef.current = { x: 0, y: 0 };
      currentPositionRef.current = { x: 0, y: 0 };
      inner.style.transition = inactiveTransition;
      inner.style.transform = "translate3d(0px, 0px, 0)";
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const magnet = magnetRef.current;
      if (!magnet) return;

      const { left, top, width, height } = magnet.getBoundingClientRect();
      const centerX = left + width / 2;
      const centerY = top + height / 2;

      const distX = Math.abs(centerX - e.clientX);
      const distY = Math.abs(centerY - e.clientY);

      if (distX < width / 2 + padding && distY < height / 2 + padding) {
        if (!isActiveRef.current) {
          inner.style.transition = activeTransition;
          isActiveRef.current = true;
        }

        targetPositionRef.current = {
          x: (e.clientX - centerX) / magnetStrength,
          y: (e.clientY - centerY) / magnetStrength,
        };
      } else {
        if (isActiveRef.current) {
          inner.style.transition = inactiveTransition;
          isActiveRef.current = false;
        }

        targetPositionRef.current = { x: 0, y: 0 };
      }

      scheduleAnimation();
    };

    const handleMouseLeave = () => {
      inner.style.transition = inactiveTransition;
      isActiveRef.current = false;
      targetPositionRef.current = { x: 0, y: 0 };
      scheduleAnimation();
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [
    activeTransition,
    disabled,
    inactiveTransition,
    magnetStrength,
    padding,
  ]);

  return (
    <div
      ref={magnetRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <div
        ref={innerRef}
        style={{
          transform: "translate3d(0px, 0px, 0)",
          transition: inactiveTransition,
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default Magnet;
